<template>
    <div
        class="remove-member-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="remove-member-modal-title"
        aria-describedby="remove-member-modal-description"
        @click.self="closeModal"
    >
        <div
            ref="dialog"
            class="remove-member-modal__dialog"
        >
            <div class="remove-member-modal__body">
                <div
                    class="remove-member-modal__icon"
                    aria-hidden="true"
                >
                    <i class="fa fa-user-times"></i>
                </div>

                <p
                    id="remove-member-modal-description"
                    class="remove-member-modal__message"
                >
                    Are you sure you want to remove
                    <strong>{{ userFullName }}</strong>
                    from
                    <strong>{{ formattedGroupName }}</strong>?
                </p>

                <p class="remove-member-modal__description">
                    This member will lose access to the group
                    and its messages.
                </p>

                <p
                    v-if="errorMessage"
                    class="remove-member-modal__error"
                    role="alert"
                    aria-live="assertive"
                >
                    <i
                        class="fa fa-exclamation-circle"
                        aria-hidden="true"
                    ></i>

                    {{ errorMessage }}
                </p>
            </div>

            <footer class="remove-member-modal__footer">
                <button
                    type="button"
                    class="
                        remove-member-modal__button
                        remove-member-modal__button--cancel
                    "
                    :disabled="isRemoving"
                    @click="closeModal"
                >
                    Cancel
                </button>

                <button
                    ref="removeButton"
                    type="button"
                    class="
                        remove-member-modal__button
                        remove-member-modal__button--remove
                    "
                    :disabled="
                        isRemoving ||
                        !hasValidUserId ||
                        !hasValidRoomId
                    "
                    @click="removeUser"
                >
                    <i
                        v-if="isRemoving"
                        class="fa fa-spinner fa-spin"
                        aria-hidden="true"
                    ></i>

                    <i
                        v-else
                        class="fa fa-user-times"
                        aria-hidden="true"
                    ></i>

                    <span>
                        {{
                            isRemoving
                                ? 'Removing...'
                                : 'Remove Member'
                        }}
                    </span>
                </button>
            </footer>
        </div>
    </div>
</template>

<script>
import { bus } from '@/main';
import toast from '@/services/toast';

const BODY_SCROLL_CLASS =
    'body_right_scroll_remove';

export default {
    name: 'RemoveUserFromGroup',

    props: {
        userInfo: {
            type: Object,
            required: true
        },

        groupInfo: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            isRemoving: false,
            errorMessage: '',
            previouslyFocusedElement: null,
            globalListenersAdded: false
        };
    },

    computed: {
        userId() {
            if (!this.userInfo) {
                return null;
            }

            if (
                this.userInfo.id !== undefined &&
                this.userInfo.id !== null
            ) {
                return this.userInfo.id;
            }

            if (
                this.userInfo.user_id !== undefined &&
                this.userInfo.user_id !== null
            ) {
                return this.userInfo.user_id;
            }

            return null;
        },

        roomId() {
            if (!this.groupInfo) {
                return null;
            }

            if (
                this.groupInfo.chat_room !== undefined &&
                this.groupInfo.chat_room !== null
            ) {
                return this.groupInfo.chat_room;
            }

            if (
                this.groupInfo.room_id !== undefined &&
                this.groupInfo.room_id !== null
            ) {
                return this.groupInfo.room_id;
            }

            if (
                this.groupInfo.id !== undefined &&
                this.groupInfo.id !== null
            ) {
                return this.groupInfo.id;
            }

            return null;
        },

        hasValidUserId() {
            return this.isValidId(this.userId);
        },

        hasValidRoomId() {
            return this.isValidId(this.roomId);
        },

        userFullName() {
            if (!this.userInfo) {
                return 'this member';
            }

            const firstName =
                this.userInfo.first_name ||
                this.userInfo.firstName ||
                '';

            const lastName =
                this.userInfo.last_name ||
                this.userInfo.lastName ||
                '';

            const fullName = [
                firstName,
                lastName
            ]
                .join(' ')
                .trim();

            const fallbackName =
                this.userInfo.full_name ||
                this.userInfo.name ||
                this.userInfo.email ||
                '';

            return (
                fullName ||
                String(fallbackName).trim() ||
                'this member'
            );
        },

        groupName() {
            if (!this.groupInfo) {
                return '';
            }

            const name =
                this.groupInfo.group_name ||
                this.groupInfo.room_name ||
                this.groupInfo.name ||
                '';

            return String(name).trim();
        },

        formattedGroupName() {
            return this.groupName
                ? '"' + this.groupName + '"'
                : 'this group';
        }
    },

    mounted() {
        this.previouslyFocusedElement =
            document.activeElement;

        this.addGlobalListeners();

        this.$nextTick(() => {
            if (this.$refs.removeButton) {
                this.$refs.removeButton.focus();
            }
        });
    },

    beforeDestroy() {
        this.removeGlobalListeners();
    },

    methods: {
        async removeUser() {
            if (this.isRemoving) {
                return;
            }

            if (
                !this.hasValidUserId ||
                !this.hasValidRoomId
            ) {
                this.errorMessage =
                    'User or group information is unavailable.';

                return;
            }

            this.isRemoving = true;
            this.errorMessage = '';

            try {
                const response =
                    await this.$store.dispatch(
                        'removeUserFromGroup',
                        {
                            data: {
                                id: this.userId,
                                data: {
                                    room_id: this.roomId
                                }
                            }
                        }
                    );

                /*
                 * Inform other chat components immediately so
                 * they may update their local group-member state.
                 */
                bus.$emit(
                    'groupMemberRemovedBus',
                    {
                        roomId: this.roomId,
                        userId: this.userId
                    }
                );

                /*
                 * Refresh failure is handled internally because
                 * the member-removal request already succeeded.
                 */
                await this.refreshGroupInfo();

                toast.success(
                    this.getSuccessMessage(
                        response,
                        this.userFullName +
                            ' was removed from the group.'
                    )
                );

                this.closeModal(true);
            } catch (error) {
                this.errorMessage =
                    this.getErrorMessage(
                        error,
                        'Unable to remove this member. Please try again.'
                    );

                console.error(
                    'Unable to remove group member:',
                    error
                );
            } finally {
                this.isRemoving = false;
            }
        },

        async refreshGroupInfo() {
            try {
                const response =
                    await this.$store.dispatch(
                        'fetchGroupChatDetailsById',
                        {
                            id: this.roomId
                        }
                    );

                const groupData =
                    this.extractResponseData(
                        response
                    );

                if (!groupData) {
                    return;
                }

                bus.$emit(
                    'updateGroupInfoDataBus',
                    groupData
                );
            } catch (error) {
                console.error(
                    'Member was removed, but group information could not be refreshed:',
                    error
                );
            }
        },

        extractResponseData(response) {
            if (!response) {
                return null;
            }

            if (
                response.data &&
                response.data.data !== undefined
            ) {
                return response.data.data;
            }

            if (response.data !== undefined) {
                return response.data;
            }

            return null;
        },

        isValidId(value) {
            return (
                value !== null &&
                value !== undefined &&
                String(value).trim() !== ''
            );
        },

        getSuccessMessage(
            response,
            fallbackMessage
        ) {
            if (!response || !response.data) {
                return fallbackMessage;
            }

            const responseData = response.data;

            if (
                responseData.data &&
                typeof responseData.data ===
                    'object'
            ) {
                return (
                    responseData.data.user_msg ||
                    responseData.data.msg ||
                    responseData.data.message ||
                    responseData.user_msg ||
                    responseData.msg ||
                    responseData.message ||
                    fallbackMessage
                );
            }

            return (
                responseData.user_msg ||
                responseData.msg ||
                responseData.message ||
                fallbackMessage
            );
        },

        getErrorMessage(
            error,
            fallbackMessage
        ) {
            const responseData =
                error &&
                error.response &&
                error.response.data
                    ? error.response.data
                    : error && error.data
                        ? error.data
                        : null;

            if (!responseData) {
                return (
                    error &&
                    error.message
                        ? error.message
                        : fallbackMessage
                );
            }

            const nestedData =
                responseData.data &&
                typeof responseData.data ===
                    'object'
                    ? responseData.data
                    : null;

            const nestedError =
                this.getFirstValidationError(
                    nestedData
                );

            if (nestedError) {
                return nestedError;
            }

            const directError =
                this.getFirstValidationError(
                    responseData
                );

            if (directError) {
                return directError;
            }

            return (
                responseData.user_msg ||
                responseData.msg ||
                responseData.message ||
                responseData.detail ||
                fallbackMessage
            );
        },

        getFirstValidationError(data) {
            if (
                !data ||
                typeof data !== 'object'
            ) {
                return '';
            }

            if (
                Array.isArray(
                    data.non_field_errors
                ) &&
                data.non_field_errors.length
            ) {
                return String(
                    data.non_field_errors[0]
                );
            }

            const keys = Object.keys(data);

            for (
                let index = 0;
                index < keys.length;
                index += 1
            ) {
                const value = data[keys[index]];

                if (
                    Array.isArray(value) &&
                    value.length
                ) {
                    return String(value[0]);
                }

                if (
                    typeof value === 'string' &&
                    value.trim()
                ) {
                    return value;
                }
            }

            return '';
        },

        handleKeydown(event) {
            if (
                event &&
                event.key === 'Escape' &&
                !this.isRemoving
            ) {
                this.closeModal();
            }
        },

        addGlobalListeners() {
            if (this.globalListenersAdded) {
                return;
            }

            document.body.classList.add(
                BODY_SCROLL_CLASS
            );

            document.addEventListener(
                'keydown',
                this.handleKeydown
            );

            this.globalListenersAdded = true;
        },

        removeGlobalListeners() {
            if (!this.globalListenersAdded) {
                return;
            }

            document.body.classList.remove(
                BODY_SCROLL_CLASS
            );

            document.removeEventListener(
                'keydown',
                this.handleKeydown
            );

            this.globalListenersAdded = false;
        },

        closeModal(forceClose) {
            if (
                this.isRemoving &&
                forceClose !== true
            ) {
                return;
            }

            this.removeGlobalListeners();

            bus.$emit(
                'removeUserPopupStateBus',
                false
            );

            this.restoreFocus();
        },

        restoreFocus() {
            const previousElement =
                this.previouslyFocusedElement;

            this.$nextTick(() => {
                if (
                    previousElement &&
                    document.body.contains(
                        previousElement
                    ) &&
                    typeof previousElement.focus ===
                        'function'
                ) {
                    previousElement.focus();
                }

                this.previouslyFocusedElement =
                    null;
            });
        }
    }
};
</script>

<style lang="scss" scoped>
$modal-overlay: rgba(0, 0, 0, 0.92);
$modal-background: #000000;
$text-primary: #ffffff;
$text-secondary: #b8b8b8;
$accent-color: #ffd500;
$danger-color: #d94848;
$danger-hover: #ff5c5c;
$cancel-background: rgba(255, 255, 255, 0.14);
$error-color: #ff6b6b;
$border-color: rgba(255, 255, 255, 0.16);

.remove-member-modal {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 15px;
    overflow-y: auto;
    background-color: $modal-overlay;
    font-family:
        'MyriadProRegular',
        'Lato',
        sans-serif;

    &__dialog {
        width: 100%;
        max-width: 520px;
        margin: auto;
        overflow: hidden;
        border-radius: 6px;
        color: $text-primary;
        background-color: $modal-background;
        box-shadow:
            0 0 13px rgba(
                255,
                213,
                0,
                0.6
            );
    }

    &__body {
        padding: 26px 20px 22px;
        text-align: center;
    }

    &__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 64px;
        height: 64px;
        margin-bottom: 16px;
        border-radius: 50%;
        color: $danger-color;
        background-color: rgba(
            217,
            72,
            72,
            0.12
        );
        font-size: 28px;
    }

    &__message {
        margin: 0;
        color: $text-primary;
        font-size: 18px;
        line-height: 1.5;
        overflow-wrap: anywhere;

        strong {
            color: #87cefa;
            font-weight: 600;
        }
    }

    &__description {
        margin: 10px 0 0;
        color: $text-secondary;
        font-size: 14px;
        line-height: 1.5;
    }

    &__error {
        margin: 18px 0 0;
        padding: 10px 12px;
        border: 1px solid rgba(
            255,
            107,
            107,
            0.35
        );
        border-radius: 4px;
        color: $error-color;
        background-color: rgba(
            255,
            107,
            107,
            0.08
        );
        font-size: 14px;
        line-height: 1.4;
        text-align: left;

        .fa {
            margin-right: 6px;
        }
    }

    &__footer {
        display: flex;
        align-items: center;
        justify-content:center;
        gap: 12px;
        padding: 0 20px 20px;
    }

    &__button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 130px;
        min-height: 40px;
        padding: 9px 16px;
        border: 0;
        border-radius: 4px;
        color: $text-primary;
        font-family: inherit;
        font-size: 16px;
        line-height: 1;
        cursor: pointer;
        box-shadow: none;
        transition:
            color 0.2s ease,
            background-color 0.2s ease,
            opacity 0.2s ease,
            transform 0.2s ease;

        .fa {
            margin-right: 7px;
        }

        &:hover:not(:disabled){
            transform: translateY(-1px);
        }

        &:focus {
            outline: 2px solid $accent-color;
            outline-offset: 2px;
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.55;
        }

        &--cancel {
            background-color: $cancel-background;

            &:hover:not(:disabled){
                color: #22292f;
                background-color: $accent-color;
            }
        }

        &--remove {
            background-color: $danger-color;

            &:hover:not(:disabled),
            &:focus:not(:disabled) {
                background-color: $danger-hover;
            }
        }
    }
}

@media only screen and (max-width: 480px) {
    .remove-member-modal {
        align-items: flex-end;
        padding: 10px;

        &__header {
            padding: 16px;
        }

        &__title {
            font-size: 18px;
        }

        &__body {
            padding: 22px 16px;
        }

        &__footer {
            flex-direction: column-reverse;
            padding: 0 16px 16px;
        }

        &__button {
            width: 100%;
        }
    }
}
</style>